import {
  EScreenType,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionParameter,
  ISymbol,
  ITargetScreenInputParam
} from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { IActionPanelStatesCanBeUpdatedFromChild } from "./action-panel";
import ActionParameterTitle from "./action-parameter-title";
import { isActionParameterShownInPanel } from "./action-parameter-visibility";
import { NavigationScreenInputPicker } from "./navigation-screen-input-picker";
import { SymbolPicker } from "./symbol-picker";

export interface IPrintReportSymbolsGroupState {}

export interface IPrintReportSymbolsGroupProps {
  action: IAction;
  parameters: IActionParameter[];
  updateValues: (value: IActionPanelStatesCanBeUpdatedFromChild) => void;
  targetScreenInputParams: ITargetScreenInputParam[];
  changeActionParameter: (parameter: IActionParameter, symbol: ISymbol) => void;
  deleteTargetScreenInputParams: (parameterName: string) => void;
}

class PrintReportSymbolsGroup extends PureSdComponent<
  IPrintReportSymbolsGroupProps & IWithSDProps & IWithContextProp,
  IPrintReportSymbolsGroupState
> {
  constructor(props: IPrintReportSymbolsGroupProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {};
  }

  deleteTargetScreenInputParams = (screenInputName: string) => {
    if (this.props.deleteTargetScreenInputParams) {
      this.props.deleteTargetScreenInputParams(screenInputName);
    }
  };

  getParameterWithName = (name: "reportName"): IActionParameter | undefined => {
    return this.props.parameters.find((x) => x.name === name);
  };

  getFromPageSelectValue = () => {
    const fromPageId = this.getParameterWithName("reportName");
    if (fromPageId?.symbol?.fixedValue && fromPageId.symbol.fixedValue !== "") return fromPageId.symbol.fixedValue;
    return "";
  };

  updateValues = (value: IActionPanelStatesCanBeUpdatedFromChild) => {
    if (this.props.updateValues !== undefined) {
      this.props.updateValues(value);
    }
  };

  onSelectToScreen = (val: string, check?: boolean): void => {
    const parameters = [...this.props.parameters];
    const fromPageParameter = parameters.find((x) => x.name === "reportName");
    if (fromPageParameter) {
      fromPageParameter.symbol.fixedValue = val;
      const targetScreenInputParams = [] as ITargetScreenInputParam[];
      const fromPageData = this.props.sd.screenList.find((x) => x.id === val);
      if (fromPageData) {
        const targetScreenInputParamsProps = _.cloneDeep(this.props.targetScreenInputParams);
        fromPageData?.publicMetaData.screenInputs?.forEach((s) => {
          if (
            !targetScreenInputParams.some(
              (x) => x.name === s.name && x.typeName === s.type && x.cardinality === s.cardinality
            )
          ) {
            const foundParam = _.cloneDeep(
              targetScreenInputParamsProps?.find(
                (y) =>
                  y.name === s.name && (y.type === s.type || y.typeName === s.type) && y.cardinality === s.cardinality
              )
            );
            if (foundParam) {
              targetScreenInputParams.push(foundParam);
            } else {
              targetScreenInputParams.push({
                name: s.name,
                typeName: s.type,
                cardinality: s.cardinality,
                symbol: {
                  description: s.name,
                  category: ESymbolCategory.fixed,
                  fixedValue: ""
                } as ISymbol
              } as ITargetScreenInputParam);
            }
          }
        });
      }
      this.updateValues({ actionParameters: parameters, targetScreenInputParams });
    }
  };

  handleNavigateOnNotificationClickVisibility = (val) => {
    this.setState({ isNavigateOnNotificationClickVisible: val });
    if (val === false) {
      const parameters = [...this.props.parameters];
      const fromPageParameter = parameters.find((x) => x.name === "reportName");
      if (fromPageParameter) {
        fromPageParameter.symbol.category = ESymbolCategory.fixed;
        fromPageParameter.symbol.fixedValue = "";
        fromPageParameter.symbol.description = "";
      }
      this.updateValues({ actionParameters: parameters });
    }
  };

  renderFromPageId = (parameter: IActionParameter): JSX.Element => {
    const parameters = [...this.props.parameters];
    let targetPageId = "";
    const fromPageParameter = parameters.find((x) => x.name === "reportName");
    if (fromPageParameter) {
      targetPageId = fromPageParameter.symbol.fixedValue;
    }
    const reportOptions: KuikaSelectboxOption[] =
      this.props.sd.screenList
        ?.sort((a, b) => a.name.localeCompare(b.name))
        ?.filter((x) => x.publicMetaData.screenType === EScreenType.Report)
        ?.map((s): KuikaSelectboxOption => ({
          key: s.id,
          value: s.id,
          label: s.name,
          testId: `${s.name}Option`
        })) ?? [];

    return (
      <>
        <div className="kuika_add_actions_properties_name">Select Report:</div>
        <div className="kuika_property-grid-column">
          <KuikaSelectbox
            searchable
            size="sm"
            style={{ width: "100%" }}
            value={this.getFromPageSelectValue()}
            onChange={(value: string): void => this.onSelectToScreen(value, false)}
            className="kuika_add_actions_inputs_size"
            data-testid="selectReportDropdown"
            options={reportOptions}
          />
        </div>
        {parameter.symbol.fixedValue && Guid.isGuid(parameter.symbol.fixedValue) && (
          <NavigationScreenInputPicker
            action={this.props.action}
            deleteTargetScreenInputParams={this.deleteTargetScreenInputParams}
            targetScreenId={targetPageId}
            targetScreenInputParams={this.props.targetScreenInputParams}
            updateTargetScreenInputParams={(targetScreenInputParams) => this.updateValues({ targetScreenInputParams })}
            screenType="Report"
          />
        )}
      </>
    );
  };

  render() {
    if (this.props.parameters.length <= 0) return null;
    return (
      <div style={{ width: "100%" }}>
        {this.props.parameters
          ?.filter((parameter: IActionParameter): boolean => isActionParameterShownInPanel(parameter))
          .map((parameter, id) => {
            return parameter.name === "reportName" ? (
              this.renderFromPageId(parameter)
            ) : (
              <div key={id}>
                <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
                  <ActionParameterTitle parameter={parameter} />
                </span>
                {
                  <SymbolPicker
                    action={this.props.action}
                    changeType="onBlur"
                    symbol={parameter.symbol}
                    valueType={parameter.Typename}
                    targetCardinality={parameter.cardinality}
                    onSelectedSymbol={(symbol: ISymbol) => this.props.changeActionParameter(parameter, symbol)}
                    bindingParameter={parameter}
                  ></SymbolPicker>
                }
              </div>
            );
          })}
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}

const printReportSymbolsGroup = withContext<IPrintReportSymbolsGroupProps>(
  withSD<IPrintReportSymbolsGroupProps>(PrintReportSymbolsGroup)
);
export { printReportSymbolsGroup as PrintReportSymbolsGroup };
